import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Collapse,
  Typography,
  Tag,
  Space,
  Card,
  Row,
  Col,
  Statistic,
  Input,
  Select,
  Switch,
  Form,
  Tooltip,
  Skeleton,
  Empty,
  Alert,
  Grid,
  message,
} from "antd";
import {
  ReadOutlined,
  FileTextOutlined,
  BlockOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ApartmentOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import { Button, Modal } from "../../../components/Common";
import { useMutations } from "../../../hooks/useApiMutation";
import { useConfirm } from "../../../hooks/useConfirm";
import { useAccess, MODULES } from "../../../hooks/useAccess";

const { Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const APP_TAG = {
  web: "blue",
  pda: "purple",
  pos: "gold",
  fuel: "volcano",
};

const APP_OPTIONS = [
  { value: "", label: "All apps" },
  { value: "web", label: "Web" },
  { value: "pda", label: "PDA" },
  { value: "pos", label: "POS" },
  { value: "fuel", label: "Fuel" },
];

const formatNumber = (n) => (n ?? 0).toLocaleString();

const formatDate = (value) =>
  value ? new Date(value).toLocaleString() : "—";

// A source that has never entered an answer is dead weight — it costs an embedding on every
// publish and buys nothing. Surfacing that here is the whole point of tracking usage.
const usageTag = (doc) =>
  doc.usageCount > 0 ? (
    <Tooltip title={`Last used ${formatDate(doc.lastUsedAt)}`}>
      <Tag color="blue">used {formatNumber(doc.usageCount)}×</Tag>
    </Tooltip>
  ) : (
    <Tooltip title="No answer has ever included a chunk from this source">
      <Tag>never used</Tag>
    </Tooltip>
  );

const KnowledgeTab = ({ agentId }) => {
  const { mutate } = useMutations();
  // mutate is recreated on every render by useMutations(). Keeping it in a ref
  // lets useCallback depend only on stable ids, breaking the effect → setState → render loop.
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;
  const { confirmAction } = useConfirm();
  const { can } = useAccess();
  const screens = useBreakpoint();
  const canEdit = can(MODULES.AGENTS, "update");

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  // Copëzat ngarkohen vetëm kur hapet dokumenti — një dokument i vetëm mund të
  // ketë dhjetëra copëza dhe asnjë nuk duhet para se dikush ta shpalosë.
  const [chunks, setChunks] = useState({});
  const chunksRef = useRef(chunks);
  chunksRef.current = chunks;
  const [loadingChunks, setLoadingChunks] = useState({});

  const [editor, setEditor] = useState(null); // null | { documentId, reconstructed }
  const [editorLoading, setEditorLoading] = useState(false);
  const [form] = Form.useForm();

  // Njohuritë e huazuara: agjentët e lidhur, dokumentet e tyre (vetëm-lexim, të
  // ngarkuara kur hapet paneli) dhe ekrani i zgjedhjes.
  const [links, setLinks] = useState([]);
  const [linkedDocs, setLinkedDocs] = useState({});
  const linkedDocsRef = useRef(linkedDocs);
  linkedDocsRef.current = linkedDocs;
  const [loadingLinkedDocs, setLoadingLinkedDocs] = useState({});
  const [linkPicker, setLinkPicker] = useState(null); // null | { options, selected, loading }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await mutateRef.current("fetchAgentKnowledge", {
        method: "get",
        endpoint: `/v1/agents/${agentId}/knowledge`,
      });
      setDocs(data?.data || []);
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  const loadLinks = useCallback(async () => {
    try {
      const { data } = await mutateRef.current("fetchAgentKnowledgeLinks", {
        method: "get",
        endpoint: `/v1/agents/${agentId}/knowledge/links`,
      });
      setLinks(data?.data || []);
    } catch {
      setLinks([]);
    }
  }, [agentId]);

  useEffect(() => {
    load();
    loadLinks();
  }, [load, loadLinks]);

  // Dokumentet e një agjenti të lidhur — vetëm ato aktive hyjnë në kërkim, ndaj
  // vetëm ato tregohen këtu: lista duhet të përputhet me atë që agjenti lexon.
  const loadLinkedDocs = useCallback(
    async (sourceAgentId) => {
      if (linkedDocsRef.current[sourceAgentId]) return;
      setLoadingLinkedDocs((prev) => ({ ...prev, [sourceAgentId]: true }));
      try {
        const { data } = await mutateRef.current("fetchLinkedAgentKnowledge", {
          method: "get",
          endpoint: `/v1/agents/${sourceAgentId}/knowledge`,
        });
        setLinkedDocs((prev) => ({
          ...prev,
          [sourceAgentId]: (data?.data || []).filter((d) => d.isActive),
        }));
      } catch {
        setLinkedDocs((prev) => ({ ...prev, [sourceAgentId]: [] }));
      } finally {
        setLoadingLinkedDocs((prev) => ({ ...prev, [sourceAgentId]: false }));
      }
    },
    []
  );

  const openLinkPicker = async () => {
    setLinkPicker({ options: [], selected: links.map((l) => l.sourceAgentId), loading: true });
    try {
      const { data } = await mutateRef.current("fetchAgentsForKnowledgeLinks", {
        method: "get",
        endpoint: "/v1/agents",
        params: { page: 1, pageSize: 200 },
      });
      const options = (data?.data?.items || [])
        .filter((a) => a.agentId !== agentId)
        .map((a) => ({
          value: a.agentId,
          label: a.companyName ? `${a.name} — ${a.companyName}` : a.name,
        }));
      setLinkPicker((prev) => (prev ? { ...prev, options, loading: false } : prev));
    } catch {
      message.error("Failed to load the assistants");
      setLinkPicker(null);
    }
  };

  const saveLinks = async () => {
    const selected = linkPicker?.selected || [];
    // mutate() nuk hedh përjashtim: dështimi kthehet te `error` dhe mesazhi i
    // serverit (p.sh. agjent i një kompanie tjetër) është shfaqur tashmë.
    const { data, error } = await mutate("saveAgentKnowledgeLinks", {
      method: "put",
      endpoint: `/v1/agents/${agentId}/knowledge/links`,
      payload: { sourceAgentIds: selected },
      alert: { success: "Linked knowledge updated." },
    });
    if (error) return;
    const saved = data?.data || [];
    setLinks(saved);
    // Vetëm lidhjet e hequra dalin nga cache-i: një panel i hapur për një lidhje
    // që mbetet duhet të vazhdojë t'i tregojë dokumentet, jo një kuti bosh.
    const kept = new Set(saved.map((l) => l.sourceAgentId));
    setLinkedDocs((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([key]) => kept.has(Number(key)))
      )
    );
    setLinkPicker(null);
  };

  const loadChunks = useCallback(
    async (documentId, force = false) => {
      if (!force && chunksRef.current[documentId]) return;
      setLoadingChunks((prev) => ({ ...prev, [documentId]: true }));
      try {
        const { data } = await mutateRef.current("fetchKnowledgeChunks", {
          method: "get",
          endpoint: `/v1/agents/${agentId}/knowledge/${documentId}/chunks`,
        });
        setChunks((prev) => ({ ...prev, [documentId]: data?.data || [] }));
      } catch {
        setChunks((prev) => ({ ...prev, [documentId]: [] }));
      } finally {
        setLoadingChunks((prev) => ({ ...prev, [documentId]: false }));
      }
    },
    [agentId]
  );

  const onExpand = (openKeys) => {
    openKeys.forEach((key) => loadChunks(Number(key)));
  };

  // Modal-i i përbashkët është destroyOnHidden, ndaj forma çmontohet sa herë
  // mbyllet: vlerat kalojnë si initialValues gjatë montimit, jo me
  // setFieldsValue mbi një formë që ende s'ekziston.
  //
  // Por store-i i Form.useForm() rri te ky komponent, që nuk çmontohet kurrë —
  // rc-field-form i shkruan initialValues në store VETËM në montimin e parë.
  // Pa këtë reset, hapja e dytë do të shfaqte dokumentin e mëparshëm, dhe
  // meqë upsert-i identifikohet nga SourceRef-i, "publikimi i ri" do të
  // mbishkruante atë të vjetrin.
  const editorValues = editor?.values;
  useEffect(() => {
    if (editorValues) form.resetFields();
  }, [editorValues, form]);

  const openCreate = () =>
    setEditor({
      documentId: null,
      reconstructed: false,
      values: {
        title: "",
        sourceRef: "",
        app: "",
        owner: "",
        isActive: true,
        content: "",
      },
    });

  const openEdit = async (doc) => {
    setEditorLoading(true);
    setEditor({ documentId: doc.knowledgeDocumentId, reconstructed: false, values: null });
    try {
      const { data } = await mutateRef.current("fetchKnowledgeDocument", {
        method: "get",
        endpoint: `/v1/agents/${agentId}/knowledge/${doc.knowledgeDocumentId}`,
      });
      const detail = data?.data;
      if (!detail) throw new Error("missing");
      setEditor({
        documentId: doc.knowledgeDocumentId,
        reconstructed: Boolean(detail.contentIsReconstructed),
        values: {
          title: detail.title,
          sourceRef: detail.sourceRef,
          app: detail.app || "",
          owner: detail.owner || "",
          isActive: detail.isActive,
          content: detail.content || "",
        },
      });
    } catch {
      message.error("Failed to load the document");
      setEditor(null);
    } finally {
      setEditorLoading(false);
    }
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      await mutate("saveKnowledgeDocument", {
        method: "put",
        endpoint: `/v1/agents/${agentId}/knowledge`,
        payload: {
          title: values.title.trim(),
          sourceRef: values.sourceRef.trim(),
          app: values.app ? values.app : null,
          owner: values.owner?.trim() || null,
          content: values.content,
          isActive: values.isActive,
        },
        alert: { success: "Knowledge published — chunks re-embedded." },
      });
      const documentId = editor?.documentId;
      setEditor(null);
      load();
      // Copëzat u rikrijuan nga e para: cache-i i vjetër i këtij dokumenti
      // do të tregonte tekstin e mëparshëm.
      if (documentId) loadChunks(documentId, true);
    } catch (err) {
      if (err?.errorFields) return;
      message.error("Failed to publish the document");
    }
  };

  const onDelete = (doc) =>
    confirmAction({
      key: "deleteKnowledgeDocument",
      method: "delete",
      endpoint: `/v1/agents/${agentId}/knowledge/${doc.knowledgeDocumentId}`,
      title: "Delete knowledge document",
      content: `"${doc.title}" and its ${doc.chunkCount} chunk(s) are removed permanently. The agent stops using this source immediately.`,
      okText: "Delete",
      okType: "danger",
      onSuccess: () => {
        setChunks((prev) => {
          const next = { ...prev };
          delete next[doc.knowledgeDocumentId];
          return next;
        });
        load();
      },
    });

  const totals = useMemo(
    () => ({
      documents: docs.length,
      active: docs.filter((d) => d.isActive).length,
      chunks: docs.reduce((sum, d) => sum + (d.chunkCount || 0), 0),
      // Copëza që nuk i takojnë këtij agjenti por kërkohen bashkë me të veten.
      borrowed: links.reduce((sum, l) => sum + (l.chunkCount || 0), 0),
    }),
    [docs, links]
  );

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return docs;
    return docs.filter(
      (d) =>
        d.title?.toLowerCase().includes(needle) ||
        d.sourceRef?.toLowerCase().includes(needle) ||
        d.owner?.toLowerCase().includes(needle)
    );
  }, [docs, filter]);

  const renderChunks = (documentId) => {
    if (loadingChunks[documentId])
      return <Skeleton active paragraph={{ rows: 3 }} />;

    const rows = chunks[documentId];
    if (!rows) return null;
    if (rows.length === 0)
      return <Empty description="No chunks stored for this document" />;

    return (
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        {rows.map((c) => (
          <Card key={c.knowledgeChunkId} size="small">
            <Space wrap size={[8, 4]} style={{ marginBottom: 8 }}>
              <Tag color="blue">#{c.chunkIndex + 1}</Tag>
              {c.embeddingModel && <Text code>{c.embeddingModel}</Text>}
              {c.embeddingDimension > 0 && (
                <Text type="secondary">{c.embeddingDimension} dim</Text>
              )}
              {c.tokensUsed != null && (
                <Text type="secondary">{formatNumber(c.tokensUsed)} tokens</Text>
              )}
              <Text type="secondary">{formatDate(c.createdAt)}</Text>
            </Space>
            <Paragraph
              style={{
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                margin: 0,
                fontSize: 13,
              }}
              ellipsis={{ rows: 6, expandable: true, symbol: "Show full chunk" }}
            >
              {c.content}
            </Paragraph>
          </Card>
        ))}
      </Space>
    );
  };

  const documentActions = (doc) =>
    canEdit ? (
      <Space
        size={4}
        // Klikimi mbi butonat s'duhet ta hapë/mbyllë panelin nën ta.
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip title="Edit">
            <Button
              variant="ghost"
              size="sm"
              icon={<EditOutlined />}
              aria-label={`Edit ${doc.title}`}
              onClick={() => openEdit(doc)}
          />
        </Tooltip>
        <Tooltip title="Delete">
            <Button
              variant="ghost"
              size="sm"
              danger
              icon={<DeleteOutlined />}
              aria-label={`Delete ${doc.title}`}
              onClick={() => onDelete(doc)}
          />
        </Tooltip>
      </Space>
    ) : null;

  const header = (
    <Row gutter={[16, 12]} align="middle" justify="space-between" wrap>
      <Col xs={24} md={16} lg={17}>
        <Space align="start" size="small">
            <ReadOutlined
              style={{ fontSize: 18, color: "var(--accent-blue)", marginTop: 4 }}
            />
          <div>
            <Text strong style={{ fontSize: 16 }}>
              Learned knowledge
            </Text>
            <div>
              <Text type="secondary">
                Curated documents split into chunks and embedded. The
                best-matching chunks are injected as context before every chat
                prompt.
              </Text>
            </div>
          </div>
        </Space>
      </Col>
      {canEdit && (
        <Col xs={24} md={8} lg={7} style={{ textAlign: screens.md ? "right" : "left" }}>
          <Button variant="primary" icon={<PlusOutlined />} onClick={openCreate} block={!screens.md}>
            Publish knowledge
          </Button>
        </Col>
      )}
    </Row>
  );

  const stats = (
    <Row gutter={[12, 12]}>
      <Col xs={12} sm={6}>
        <Card size="small" styles={{ body: { padding: 12 } }}>
          <Statistic
            title="Documents"
            value={totals.documents}
            prefix={<FileTextOutlined />}
            valueStyle={{ fontSize: 22 }}
          />
        </Card>
      </Col>
      <Col xs={12} sm={6}>
        <Card size="small" styles={{ body: { padding: 12 } }}>
          <Statistic
            title="Active"
            value={totals.active}
            suffix={`/ ${totals.documents}`}
            prefix={<CheckCircleOutlined />}
            valueStyle={{ fontSize: 22 }}
          />
        </Card>
      </Col>
      <Col xs={12} sm={6}>
        <Card size="small" styles={{ body: { padding: 12 } }}>
          <Statistic
            title="Chunks"
            value={totals.chunks}
            prefix={<BlockOutlined />}
            valueStyle={{ fontSize: 22 }}
          />
        </Card>
      </Col>
      <Col xs={12} sm={6}>
        <Card size="small" styles={{ body: { padding: 12 } }}>
          <Statistic
            title="Borrowed chunks"
            value={totals.borrowed}
            prefix={<ShareAltOutlined />}
            valueStyle={{ fontSize: 22 }}
          />
        </Card>
      </Col>
    </Row>
  );

  const editorModal = (
    <Modal
      open={Boolean(editor)}
      onClose={() => setEditor(null)}
      onConfirm={onSave}
      title={editor?.documentId ? "Edit knowledge document" : "Publish knowledge"}
      confirmText="Publish"
      width={860}
    >
      {editorLoading || !editor?.values ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <>
          {editor?.reconstructed && (
            <Alert
              type="warning"
              showIcon
              message="Source text rebuilt from chunks"
              description="This document was published before the source text was kept. The text below was stitched back together from its chunks — review it before saving."
              style={{ marginBottom: 16 }}
            />
          )}
          <Alert
            type="info"
            showIcon
            message="Saving re-embeds the whole document"
            description="Every chunk is embedded again, which costs tokens and takes a few seconds for long texts. Documents are matched by source reference — reusing one replaces its content."
            style={{ marginBottom: 16 }}
          />
          <Form
            form={form}
            layout="vertical"
            requiredMark="optional"
            initialValues={editor.values}
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="title"
                  label="Title"
                  rules={[{ required: true, message: "Title is required" }]}
                >
                  <Input placeholder="Importi Inteligjent — si përdoret" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="sourceRef"
                  label="Source reference"
                  tooltip="The citable source the agent quotes, and the key that identifies the document. Changing it publishes a new document instead of updating this one."
                  rules={[
                    { required: true, message: "Source reference is required" },
                    // Upsert-i identifikohet nga SourceRef-i: një përputhje e
                    // rastësishme zëvendëson një dokument tjetër pa pyetur.
                    ...(editor?.documentId
                      ? []
                      : [
                          {
                            warningOnly: true,
                            validator: (_, value) => {
                              const needle = value?.trim().toLowerCase();
                              const clash = needle
                                ? docs.find(
                                    (d) =>
                                      d.sourceRef?.trim().toLowerCase() === needle
                                  )
                                : null;
                              return clash
                                ? Promise.reject(
                                    new Error(
                                      `Already used by "${clash.title}" — publishing replaces that document instead of creating a new one.`
                                    )
                                  )
                                : Promise.resolve();
                            },
                          },
                        ]),
                  ]}
                >
                  <Input
                    placeholder="kb/importi-inteligjent.md"
                    disabled={Boolean(editor?.documentId)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="app" label="Applies to">
                  <Select options={APP_OPTIONS} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={10}>
                <Form.Item name="owner" label="Owner">
                  <Input placeholder="KuBIT Suporti" />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item
                  name="isActive"
                  label="Active"
                  valuePropName="checked"
                  tooltip="Inactive documents stay stored but are never searched or injected."
                >
                  <Switch checkedChildren="On" unCheckedChildren="Off" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="content"
              label="Content"
              rules={[{ required: true, message: "Content is required" }]}
            >
              <Input.TextArea
                rows={14}
                placeholder="Paste the full document. Blank lines separate paragraphs and drive how it is chunked."
                style={{ fontFamily: "monospace", fontSize: 13 }}
              />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );

  const renderLinkedDocs = (sourceAgentId) => {
    if (loadingLinkedDocs[sourceAgentId])
      return <Skeleton active paragraph={{ rows: 2 }} />;

    const rows = linkedDocs[sourceAgentId];
    if (!rows) return null;
    if (rows.length === 0)
      return <Empty description="This assistant has no active knowledge to share" />;

    return (
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        {rows.map((d) => (
          <Card key={d.knowledgeDocumentId} size="small">
            <Space wrap size={[8, 4]}>
              <Text strong style={{ overflowWrap: "anywhere" }}>
                {d.title}
              </Text>
              <Tag>{d.chunkCount} chunks</Tag>
              {d.app ? (
                <Tag color={APP_TAG[d.app] || "default"}>{d.app}</Tag>
              ) : (
                <Tag>all apps</Tag>
              )}
            </Space>
            <div>
              <Text type="secondary" style={{ fontSize: 12, overflowWrap: "anywhere" }}>
                {d.sourceRef}
              </Text>
            </div>
          </Card>
        ))}
      </Space>
    );
  };

  const linkedSection = (
    <Card size="small" style={{ marginTop: 16 }}>
      <Row gutter={[16, 12]} align="middle" justify="space-between" wrap>
        <Col xs={24} md={16} lg={17}>
          <Space align="start" size="small">
            <ApartmentOutlined
              style={{ fontSize: 18, color: "var(--accent-teal)", marginTop: 4 }}
            />
            <div>
              <Text strong style={{ fontSize: 15 }}>
                Knowledge from other assistants
              </Text>
              <div>
                <Text type="secondary">
                  Documents published on the linked assistants are searched together
                  with this one&apos;s. Nothing is copied — the owner keeps the source,
                  edits show up here at once, and answers cite the assistant they came
                  from. Linking is one level: an assistant does not pass on what it
                  borrows from someone else.
                </Text>
              </div>
            </div>
          </Space>
        </Col>
        {canEdit && (
          <Col xs={24} md={8} lg={7} style={{ textAlign: screens.md ? "right" : "left" }}>
            <Button
              variant="secondary"
              icon={<ShareAltOutlined />}
              onClick={openLinkPicker}
              block={!screens.md}
            >
              {links.length > 0 ? "Edit links" : "Link assistants"}
            </Button>
          </Col>
        )}
      </Row>

      <div style={{ marginTop: 12 }}>
        {links.length === 0 ? (
          <Text type="secondary">
            Not linked to any assistant — only this assistant&apos;s own knowledge is
            searched.
          </Text>
        ) : (
          <Collapse
            onChange={(openKeys) =>
              openKeys.forEach((key) => loadLinkedDocs(Number(key)))
            }
            items={links.map((l) => ({
              key: String(l.sourceAgentId),
              label: (
                <Space direction="vertical" size={2} style={{ display: "flex" }}>
                  <Space wrap size={[8, 4]}>
                    <Text strong style={{ overflowWrap: "anywhere" }}>
                      {l.sourceAgentName}
                    </Text>
                    <Tag color="green">{l.documentCount} documents</Tag>
                    <Tag>{l.chunkCount} chunks</Tag>
                    {l.sourceCompanyName ? (
                      <Tag color="purple">{l.sourceCompanyName}</Tag>
                    ) : (
                      <Tag>global</Tag>
                    )}
                  </Space>
                  {l.sourceAgentDescription && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {l.sourceAgentDescription}
                    </Text>
                  )}
                </Space>
              ),
              children: renderLinkedDocs(l.sourceAgentId),
            }))}
          />
        )}
      </div>
    </Card>
  );

  const linkPickerModal = (
    <Modal
      open={Boolean(linkPicker)}
      onClose={() => setLinkPicker(null)}
      onConfirm={saveLinks}
      title="Link knowledge from other assistants"
      confirmText="Save links"
      width={620}
    >
      {linkPicker?.loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            message="Read-only borrowing"
            description="This assistant reads the linked assistants' active documents; it cannot edit or delete them. Only global assistants and assistants of the same company can be linked."
            style={{ marginBottom: 16 }}
          />
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Pick the assistants to read knowledge from"
            style={{ width: "100%" }}
            value={linkPicker?.selected || []}
            options={linkPicker?.options || []}
            onChange={(selected) =>
              setLinkPicker((prev) => (prev ? { ...prev, selected } : prev))
            }
          />
        </>
      )}
    </Modal>
  );

  if (loading) return <Skeleton active paragraph={{ rows: 6 }} />;

  if (docs.length === 0)
    return (
      <div>
        {header}
        <Empty
          description="Nothing learned yet"
          style={{ marginTop: 48, marginBottom: 24 }}
        >
          <Text type="secondary">
            Publish a document — or link another assistant below — and the agent
            starts citing it in chat.
          </Text>
        </Empty>
        {linkedSection}
        {editorModal}
        {linkPickerModal}
      </div>
    );

  return (
    <div>
      {header}

      <div style={{ marginTop: 16 }}>{stats}</div>

      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Filter by title, source or owner"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={{ maxWidth: 360, width: "100%", margin: "16px 0" }}
      />

      {visible.length === 0 ? (
        <Empty description="No documents match this filter" />
      ) : (
        <Collapse
          onChange={onExpand}
          items={visible.map((d) => ({
            key: String(d.knowledgeDocumentId),
            extra: documentActions(d),
            label: (
              <Space direction="vertical" size={2} style={{ display: "flex" }}>
                <Space wrap size={[8, 4]}>
                  <Text strong style={{ overflowWrap: "anywhere" }}>
                    {d.title}
                  </Text>
                  <Tag>{d.chunkCount} chunks</Tag>
                  {usageTag(d)}
                  {d.app ? (
                    <Tag color={APP_TAG[d.app] || "default"}>{d.app}</Tag>
                  ) : (
                    <Tag>all apps</Tag>
                  )}
                  {!d.isActive && <Tag color="warning">Inactive</Tag>}
                </Space>
                <Text type="secondary" style={{ fontSize: 12, overflowWrap: "anywhere" }}>
                  {d.sourceRef}
                </Text>
              </Space>
            ),
            children: (
              <div>
                <Space wrap size={[16, 4]} style={{ marginBottom: 12 }}>
                  {d.owner && <Text type="secondary">Owner: {d.owner}</Text>}
                  <Text type="secondary">Added {formatDate(d.createdAt)}</Text>
                  {d.updatedAt && (
                    <Text type="secondary">Updated {formatDate(d.updatedAt)}</Text>
                  )}
                </Space>
                {renderChunks(d.knowledgeDocumentId)}
              </div>
            ),
          }))}
        />
      )}

      {linkedSection}

      {editorModal}
      {linkPickerModal}
    </div>
  );
};

export default KnowledgeTab;
